/**
 * CODEX's **LANCERS** section, as pure data (`decisions/0205` points 4, 6–8).
 *
 * A lancer is a roster pilot owned by a player slot. Its lore lives in two places joined by
 * pilot id: the GM's record is a codex `person` entry carrying `pilot: <pilotId>`, and the
 * player's own words are the pilot's `background`. The section lists every lancer whether or
 * not an entry names it, so a codex entry never has to exist just to show a lancer.
 *
 * {@link Lancer} is the slice of a pilot the section shows, already resolved to names
 * (`useLancers` does the compendium and slot lookups); everything here is a function of it
 * and the codex entries, with no React and no compendium.
 */
import type { CodexEntry, PilotData } from "@lancer-vtt/domain";

/**
 * The roster pilots that are lancers: owned by a player slot (F4). A pilot with no owner, or
 * owned by the GM's slot, is an NPC the GM built (prod's `Aurelio`), not a lancer. Sorted by
 * callsign, case-insensitively, then id, so the section's order doesn't follow creation order.
 */
export function lancerPilots(pilots: Record<string, PilotData>, playerSlotIds: ReadonlySet<string>): PilotData[] {
  return Object.values(pilots)
    .filter((p) => p.owner !== null && playerSlotIds.has(p.owner))
    .sort((a, b) => a.callsign.localeCompare(b.callsign, undefined, { sensitivity: "base" }) || a.id.localeCompare(b.id));
}

/** One lancer, resolved for display. Every field is one `decisions/0140` already shows other players. */
export type Lancer = {
  pilotId: string;
  callsign: string;
  /** The pilot's own name, `""` when the player left it blank. */
  name: string;
  /** The owning player slot's display name. */
  player: string;
  /** The pilot's background, in the player's words; absent when blank. */
  background?: string;
  /** The pilot's uploaded portrait URL; absent when none was uploaded. */
  image?: string;
  level: number;
  /** The active mech's name and frame name; `null` when the pilot has no mech. */
  mech: { name: string; frame: string } | null;
  /** Owned talent names. */
  talents: string[];
  /** Owned skill-trigger names. */
  triggers: string[];
  /** Whether the viewer owns this pilot (their EDIT IN PILOT line). */
  own: boolean;
};

/** A lancer joined with the codex entry that files them, if one does. */
export type LancerRow = {
  /** The row's selection id: the entry's id when filed, else the pilot's id. */
  key: string;
  lancer: Lancer;
  entry?: CodexEntry;
};

/** Joins each lancer to the `person` entry naming its pilot, in the lancers' order. */
export function joinLancers(lancers: readonly Lancer[], entries: readonly CodexEntry[]): LancerRow[] {
  const filedBy = new Map<string, CodexEntry>();
  for (const e of entries) if (e.kind === "person" && e.pilot !== undefined) filedBy.set(e.pilot, e);
  return lancers.map((lancer) => {
    const entry = filedBy.get(lancer.pilotId);
    return entry === undefined ? { key: lancer.pilotId, lancer } : { key: entry.id, lancer, entry };
  });
}

/**
 * The lancer row a selection id names: by its key, or by its pilot id (a filed lancer reached
 * through `/codex/rolodex/<pilotId>` still opens its file).
 */
export function findLancerRow(rows: readonly LancerRow[], id: string): LancerRow | undefined {
  return rows.find((r) => r.key === id) ?? rows.find((r) => r.lancer.pilotId === id);
}

/** The OPEN button's sub-line and WHAT THE BUILD SAYS's mech cell: `NAME · FRAME`, or a no-mech line. */
export function mechLine(lancer: Lancer): string {
  if (lancer.mech === null) return "NO MECH ON FILE";
  const name = lancer.mech.name.toUpperCase();
  const frame = lancer.mech.frame.toUpperCase();
  return frame === "" || frame === name ? name : `${name} · ${frame}`;
}

/** The lancer file's FILE reference, `UCA/R10 · LANCER · <CALLSIGN>` (canvas D1). */
export function lancerFileReference(lancer: Lancer): string {
  return `UCA/R10 · LANCER · ${lancer.callsign.toUpperCase().trim().replace(/\s+/g, "-")}`;
}

/**
 * The GM's starting point for a lancer's file: the entry to add to the served codex file,
 * with the pilot id filled in. One line of JSON, so it reads in the no-record box and pastes
 * into `entries` as is.
 */
export function lancerEntrySkeleton(lancer: Lancer): string {
  const id = lancer.callsign.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "lancer";
  const name = lancer.name.trim() || lancer.callsign;
  return JSON.stringify({ id, kind: "person", name, pilot: lancer.pilotId, facts: [] });
}
